import { HubConnection, HubConnectionBuilder, HubConnectionState, HttpTransportType, LogLevel } from '@microsoft/signalr';
import { getAccessToken, getActiveSession } from '../../../shared/services/authSession';
import { normalizeConversation, normalizeMessage, normalizeParticipant } from './conversationService';
import { Conversation, ConversationKind, Message } from '../types/Conversation';

interface StoredAuthState {
  tenantId: string;
}

interface MessageCreatedEvent {
  tenantId: string;
  conversationId: string;
  message: Message;
  timestamp: string;
}

interface ConversationUpdatedEvent {
  tenantId: string;
  conversationId: string;
  conversation: Conversation;
  timestamp: string;
}

interface ConversationMessagePreview {
  id: string;
  sender: Message['sender'];
  content: string;
  timestamp: string;
  status: Message['status'];
  source: Message['source'];
  messageType: Message['messageType'];
  channelContext?: Message['channelContext'];
}

interface ConversationListItem {
  id: string;
  title: string | null;
  updatedAt: string;
  isSigned: boolean;
  isAiAssistantMuted: boolean;
  isFavorite: boolean;
  isPinned: boolean;
  isSpam: boolean;
  isHidden: boolean;
  note: string | null;
  assignedToEmployeeId: string | null;
  assignedToEmployeeName: string | null;
  lastMessage: ConversationMessagePreview | null;
  unreadCountByParticipant: Record<string, number>;
  lastMessageReactionSummary: Record<string, number>;
  hasExternalParticipant: boolean;
  conversationKind: ConversationKind;
  containsWhatsAppMessages: boolean;
  containsInstagramMessages: boolean;
  containsInstagramComments: boolean;
  containsMessengerComments: boolean;
}

interface ConversationListItemUpdatedEvent {
  tenantId: string;
  conversationId: string;
  item: ConversationListItem;
  timestamp: string;
}

export type ConversationNotificationType = 'message_received' | 'added_to_conversation' | 'incoming_call';

export interface ConversationNotificationEvent {
  tenantId: string;
  conversationId: string;
  type: ConversationNotificationType;
  title: string;
  body: string;
  messageId: string | null;
  actor: Message['sender'] | null;
  timestamp: string;
}

export interface NotificationCreatedEvent {
  tenantId: string;
  recipientEmployeeId: string;
  notificationId: string;
  type: string;
  title: string;
  body: string;
  linkUrl: string | null;
  isRead: boolean;
  createdAtUtc: string;
}

export interface CallSessionUpdatedEvent {
  tenantId: string;
  session: Record<string, unknown>;
  timestamp: string;
}

interface SubscribeTenantOptions {
  tenantId: string;
  onConversationListItemUpdated?: (evt: ConversationListItemUpdatedEvent) => void;
  onConversationNotification?: (evt: ConversationNotificationEvent) => void;
  onNotificationCreated?: (evt: NotificationCreatedEvent) => void;
  onCallSessionUpdated?: (evt: CallSessionUpdatedEvent) => void;
  onReconnected?: () => void | Promise<void>;
}

interface SubscribeOptions {
  tenantId: string;
  conversationId: string;
  onMessageCreated: (evt: MessageCreatedEvent) => void;
  onConversationUpdated: (evt: ConversationUpdatedEvent) => void;
  onAiThinkingStep?: (evt: AiThinkingStepEvent) => void;
  onReconnected?: () => void | Promise<void>;
}

export interface AiThinkingStepEvent {
  tenantId: string;
  conversationId: string;
  runId?: string | null;
  step: string;
  stepKey?: string | null;
  detail: string | null;
  timestamp: string;
}

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

let connection: HubConnection | null = null;
let connectionStartPromise: Promise<void> | null = null;
let lastStartError: Error | null = null;
let connectionAuthFingerprint = '';
const activeSubscriptions = new Map<string, number>();
const activeTenantSubscriptions = new Map<string, number>();
const reconnectHandlers = new Map<string, () => void | Promise<void>>();
const tenantReconnectHandlers = new Map<string, () => void | Promise<void>>();

const getSubscriptionKey = (tenantId: string, conversationId: string) => `${tenantId}:${conversationId}`;

const getErrorText = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error);
  } catch {
    return 'Unknown error';
  }
};

const toRealtimeError = (phase: string, error: unknown): Error => {
  const message = getErrorText(error);

  if (/tenant_forbidden/i.test(message)) {
    return new Error('Realtime subscribe failed: TENANT_FORBIDDEN for the current user/token.');
  }

  if (/not_found/i.test(message)) {
    return new Error('Realtime subscribe failed: conversation was not found or not visible.');
  }

  if (/unauthorized|401|forbidden|403|jwt|token/i.test(message)) {
    return new Error('Realtime connection failed: authentication/token is invalid or expired.');
  }

  if (/cors|failed to fetch|network|negotiate|websocket|connection/i.test(message)) {
    return new Error('Realtime connection failed during negotiate/websocket (likely CORS or hub endpoint issue).');
  }

  return new Error(`Realtime ${phase} failed: ${message}`);
};

const readAuthState = (): StoredAuthState | null => {
  const session = getActiveSession();
  if (!session?.tenantId) {
    return null;
  }

  return {
    tenantId: session.tenantId,
  };
};

const buildHubUrl = () => {
  const auth = readAuthState();
  const baseHubUrl = `${API_BASE_URL}/api/v1/conversations/hub`;

  if (!auth?.tenantId) return baseHubUrl;

  const separator = baseHubUrl.includes('?') ? '&' : '?';
  return `${baseHubUrl}${separator}tenantId=${encodeURIComponent(auth.tenantId)}`;
};

const getConnectionAuthFingerprint = (): string => {
  const auth = readAuthState();
  const token = getAccessToken();
  if (!auth?.tenantId || !token) {
    return '';
  }

  return `${auth.tenantId}:${token}`;
};

const disposeConnection = async (): Promise<void> => {
  const hub = connection;
  connection = null;
  connectionStartPromise = null;
  connectionAuthFingerprint = '';
  lastStartError = null;

  if (!hub) {
    return;
  }

  try {
    if (hub.state !== HubConnectionState.Disconnected) {
      await hub.stop();
    }
  } catch (error) {
    console.warn('[conversationHub] dispose failed', error);
  }
};

export const resetConversationHubConnection = async (): Promise<void> => {
  await disposeConnection();
};

const waitForHubConnection = async (hub: HubConnection, timeoutMs: number): Promise<void> => {
  const startedAt = Date.now();

  while (Date.now() - startedAt <= timeoutMs) {
    if (hub.state === HubConnectionState.Connected) {
      return;
    }

    if (hub.state === HubConnectionState.Disconnected) {
      throw lastStartError ?? new Error('Realtime connection became disconnected while waiting to connect.');
    }

    await new Promise<void>(resolve => {
      window.setTimeout(resolve, 100);
    });
  }

  throw new Error('Timed out waiting for realtime connection to become ready.');
};

const ensureConnection = (): HubConnection => {
  const nextFingerprint = getConnectionAuthFingerprint();
  if (connection && nextFingerprint !== connectionAuthFingerprint) {
    void disposeConnection();
  }

  if (connection) return connection;

  connectionAuthFingerprint = nextFingerprint;
  connection = new HubConnectionBuilder()
    .withUrl(buildHubUrl(), {
      transport: HttpTransportType.LongPolling,
      withCredentials: false,
      accessTokenFactory: () => getAccessToken() || '',
    })
    .withAutomaticReconnect()
    .configureLogging(LogLevel.Warning)
    .build();

  connection.onreconnecting(() => {
    console.info('[conversationHub] reconnecting');
  });

  connection.onreconnected(async () => {
    console.info('[conversationHub] reconnected');

    const uniqueTenants = Array.from(activeTenantSubscriptions.keys());
    await Promise.all(
      uniqueTenants.map(async (tenantId) => {
        try {
          await connection?.invoke('SubscribeTenant', tenantId);
        } catch (error) {
          console.error('[conversationHub] re-subscribe tenant failed', { tenantId, error });
        }
      })
    );

    const uniqueSubscriptions = Array.from(activeSubscriptions.keys());
    await Promise.all(
      uniqueSubscriptions.map(async (key) => {
        const [tenantId, conversationIdRaw] = key.split(':');
        const conversationId = conversationIdRaw?.trim();
        if (!tenantId || !conversationId) return;
        try {
          await connection?.invoke('SubscribeConversation', tenantId, conversationId);
        } catch (error) {
          console.error('[conversationHub] re-subscribe failed', { tenantId, conversationId, error });
        }
      })
    );

    await Promise.all(
      Array.from(tenantReconnectHandlers.values()).map(async (callback) => {
        try {
          await callback();
        } catch (error) {
          console.error('[conversationHub] tenant reconnect callback failed', error);
        }
      })
    );

    await Promise.all(
      Array.from(reconnectHandlers.values()).map(async (callback) => {
        try {
          await callback();
        } catch (error) {
          console.error('[conversationHub] reconnect callback failed', error);
        }
      })
    );
  });

  connection.onclose((error) => {
    if (error) {
      lastStartError = toRealtimeError('connect', error);
      console.warn('[conversationHub] connection closed', error);
      return;
    }

    console.warn('[conversationHub] connection closed');
  });

  return connection;
};

const startConnection = async () => {
  if (!readAuthState()?.tenantId || !getAccessToken()) {
    throw new Error('Realtime connection failed: authentication/token is invalid or expired.');
  }

  const hub = ensureConnection();
  if (hub.state === HubConnectionState.Connected) return;

  if (connectionStartPromise) {
    await connectionStartPromise;
    const connectedHub = ensureConnection();
    if (connectedHub.state !== HubConnectionState.Connected) {
      throw lastStartError ?? new Error('Realtime connection became disconnected while waiting to connect.');
    }
    return;
  }

  connectionStartPromise = (async () => {
    lastStartError = null;
    const activeHub = ensureConnection();

    if (activeHub.state === HubConnectionState.Connected) {
      return;
    }

    if (activeHub.state === HubConnectionState.Connecting || activeHub.state === HubConnectionState.Reconnecting) {
      await waitForHubConnection(activeHub, 10000);
      return;
    }

    try {
      console.info('[conversationHub] starting connection', { hubUrl: buildHubUrl() });
      await activeHub.start();
      console.info('[conversationHub] connection started');
    } catch (error) {
      lastStartError = toRealtimeError('connect', error);
      await disposeConnection();
      throw lastStartError;
    }
  })();

  try {
    await connectionStartPromise;
  } finally {
    connectionStartPromise = null;
  }
};

const normalizeMessageCreated = (payload: any): MessageCreatedEvent => ({
  tenantId: String(payload?.tenantId ?? ''),
  conversationId: String(payload?.conversationId ?? ''),
  message: normalizeMessage(payload?.message || {}),
  timestamp: String(payload?.timestamp ?? payload?.message?.timestamp ?? new Date().toISOString()),
});

const normalizeConversationUpdated = (payload: any): ConversationUpdatedEvent => ({
  tenantId: String(payload?.tenantId ?? ''),
  conversationId: String(payload?.conversationId ?? ''),
  conversation: normalizeConversation(payload?.conversation || {}),
  timestamp: String(payload?.timestamp ?? payload?.conversation?.updatedAt ?? new Date().toISOString()),
});

const normalizeConversationMessagePreview = (value: any): ConversationMessagePreview => ({
  id: String(value?.id ?? ''),
  sender: normalizeParticipant(value?.sender),
  content: String(value?.content ?? ''),
  timestamp: String(value?.timestamp ?? new Date().toISOString()),
  status: value?.status === 'delivered' || value?.status === 'failed' ? value.status : 'sent',
  source: typeof value?.source === 'string' && value.source.trim() ? value.source.trim().toLowerCase() : 'internal',
  messageType: typeof value?.messageType === 'string' && value.messageType.trim() ? value.messageType.trim().toLowerCase() : 'text',
  channelContext: value?.channelContext && typeof value.channelContext === 'object'
    ? {
        surface: value.channelContext.surface === 'live_comment' ? 'live_comment' : 'comment',
        commentId: value.channelContext.commentId ? String(value.channelContext.commentId) : undefined,
        mediaId: value.channelContext.mediaId ? String(value.channelContext.mediaId) : undefined,
        mediaProductType: value.channelContext.mediaProductType ? String(value.channelContext.mediaProductType) : undefined,
        instagramUsername: value.channelContext.instagramUsername ? String(value.channelContext.instagramUsername) : undefined,
        webhookField: value.channelContext.webhookField ? String(value.channelContext.webhookField) : undefined,
      }
    : undefined,
});

const normalizeConversationKind = (value: any): ConversationKind => {
  const normalized = String(value ?? '').trim().toLowerCase();
  switch (normalized) {
    case 'internal-direct':
    case 'internal-channel':
    case 'external-direct':
    case 'external-channel':
      return normalized;
    default:
      return 'unknown';
  }
};

const normalizeConversationListItemUpdated = (payload: any): ConversationListItemUpdatedEvent => ({
  tenantId: String(payload?.tenantId ?? ''),
  conversationId: String(payload?.conversationId ?? ''),
  item: {
    id: String(payload?.item?.id ?? payload?.conversationId ?? ''),
    title: payload?.item?.title == null ? null : String(payload.item.title),
    updatedAt: String(payload?.item?.updatedAt ?? payload?.timestamp ?? new Date().toISOString()),
    isSigned: Boolean(payload?.item?.isSigned),
    isAiAssistantMuted: Boolean(payload?.item?.isAiAssistantMuted),
    isFavorite: Boolean(payload?.item?.isFavorite),
    isPinned: Boolean(payload?.item?.isPinned),
    isSpam: Boolean(payload?.item?.isSpam ?? payload?.item?.isHidden),
    isHidden: Boolean(payload?.item?.isSpam ?? payload?.item?.isHidden),
    note: payload?.item?.note == null ? null : String(payload.item.note),
    assignedToEmployeeId: payload?.item?.assignedToEmployeeId == null ? null : String(payload.item.assignedToEmployeeId),
    assignedToEmployeeName: payload?.item?.assignedToEmployeeName == null ? null : String(payload.item.assignedToEmployeeName),
    lastMessage: payload?.item?.lastMessage ? normalizeConversationMessagePreview(payload.item.lastMessage) : null,
    unreadCountByParticipant: payload?.item?.unreadCountByParticipant && typeof payload.item.unreadCountByParticipant === 'object'
      ? payload.item.unreadCountByParticipant
      : {},
    lastMessageReactionSummary: payload?.item?.lastMessageReactionSummary && typeof payload.item.lastMessageReactionSummary === 'object'
      ? payload.item.lastMessageReactionSummary
      : {},
    hasExternalParticipant: Boolean(payload?.item?.hasExternalParticipant),
    conversationKind: normalizeConversationKind(payload?.item?.conversationKind),
    containsWhatsAppMessages: Boolean(payload?.item?.containsWhatsAppMessages),
    containsInstagramMessages: Boolean(payload?.item?.containsInstagramMessages),
    containsInstagramComments: Boolean(payload?.item?.containsInstagramComments),
    containsMessengerComments: Boolean(payload?.item?.containsMessengerComments),
  },
  timestamp: String(payload?.timestamp ?? payload?.item?.updatedAt ?? new Date().toISOString()),
});

const normalizeConversationNotification = (payload: any): ConversationNotificationEvent => {
  const rawType = String(payload?.type ?? '').trim().toLowerCase();
  const type: ConversationNotificationType = rawType === 'addedtoconversation' || rawType === 'added_to_conversation'
    ? 'added_to_conversation'
    : rawType === 'incomingcall' || rawType === 'incoming_call'
      ? 'incoming_call'
      : 'message_received';

  return {
    tenantId: String(payload?.tenantId ?? ''),
    conversationId: String(payload?.conversationId ?? ''),
    type,
    title: String(payload?.title ?? '').trim(),
    body: String(payload?.body ?? '').trim(),
    messageId: payload?.messageId == null ? null : String(payload.messageId),
    actor: payload?.actor ? normalizeParticipant(payload.actor) : null,
    timestamp: String(payload?.timestamp ?? new Date().toISOString()),
  };
};

const normalizeNotificationCreated = (payload: any): NotificationCreatedEvent => ({
  tenantId: String(payload?.tenantId ?? ''),
  recipientEmployeeId: String(payload?.recipientEmployeeId ?? ''),
  notificationId: String(payload?.notificationId ?? ''),
  type: String(payload?.type ?? '').trim(),
  title: String(payload?.title ?? '').trim(),
  body: String(payload?.body ?? '').trim(),
  linkUrl: payload?.linkUrl == null ? null : String(payload.linkUrl),
  isRead: Boolean(payload?.isRead),
  createdAtUtc: String(payload?.createdAtUtc ?? new Date().toISOString()),
});

export const subscribeToTenant = async ({
  tenantId,
  onConversationListItemUpdated,
  onConversationNotification,
  onNotificationCreated,
  onCallSessionUpdated,
  onReconnected,
}: SubscribeTenantOptions): Promise<() => Promise<void>> => {
  const hub = ensureConnection();
  const normalizedTenantId = tenantId.trim();

  if (!normalizedTenantId) {
    throw new Error('Realtime tenant subscribe failed: tenant id is missing.');
  }

  await startConnection();

  activeTenantSubscriptions.set(normalizedTenantId, (activeTenantSubscriptions.get(normalizedTenantId) || 0) + 1);

  if (onReconnected) {
    tenantReconnectHandlers.set(normalizedTenantId, onReconnected);
  }

  const listHandler = (raw: any) => {
    if (!onConversationListItemUpdated) {
      return;
    }

    const evt = normalizeConversationListItemUpdated(raw);
    onConversationListItemUpdated(evt);
  };

  const notificationHandler = (raw: any) => {
    if (!onConversationNotification) {
      return;
    }

    const evt = normalizeConversationNotification(raw);
    onConversationNotification(evt);
  };

  const notificationCreatedHandler = (raw: any) => {
    if (!onNotificationCreated) {
      return;
    }

    const evt = normalizeNotificationCreated(raw);
    onNotificationCreated(evt);
  };

  const callSessionHandler = (raw: any) => {
    if (!onCallSessionUpdated) {
      return;
    }

    onCallSessionUpdated({
      tenantId: String(raw?.tenantId ?? ''),
      session: raw?.session && typeof raw.session === 'object' ? raw.session : {},
      timestamp: String(raw?.timestamp ?? new Date().toISOString()),
    });
  };

  hub.on('conversationListItemUpdated', listHandler);
  hub.on('conversationNotification', notificationHandler);
  hub.on('notificationCreated', notificationCreatedHandler);
  hub.on('callSessionUpdated', callSessionHandler);

  try {
    await hub.invoke('SubscribeTenant', normalizedTenantId);
  } catch (error) {
    hub.off('conversationListItemUpdated', listHandler);
    hub.off('conversationNotification', notificationHandler);
    hub.off('notificationCreated', notificationCreatedHandler);
    hub.off('callSessionUpdated', callSessionHandler);

    const currentCount = activeTenantSubscriptions.get(normalizedTenantId) || 0;
    if (currentCount <= 1) {
      activeTenantSubscriptions.delete(normalizedTenantId);
      tenantReconnectHandlers.delete(normalizedTenantId);
    } else {
      activeTenantSubscriptions.set(normalizedTenantId, currentCount - 1);
    }

    throw toRealtimeError('tenant subscribe', error);
  }

  return async () => {
    hub.off('conversationListItemUpdated', listHandler);
    hub.off('conversationNotification', notificationHandler);
    hub.off('notificationCreated', notificationCreatedHandler);
    hub.off('callSessionUpdated', callSessionHandler);

    const currentCount = activeTenantSubscriptions.get(normalizedTenantId) || 0;
    if (currentCount <= 1) {
      activeTenantSubscriptions.delete(normalizedTenantId);
      tenantReconnectHandlers.delete(normalizedTenantId);
      if (hub.state === HubConnectionState.Connected) {
        try {
          await hub.invoke('UnsubscribeTenant', normalizedTenantId);
        } catch (error) {
          console.warn('[conversationHub] unsubscribe tenant failed', { tenantId: normalizedTenantId, error });
        }
      }
    } else {
      activeTenantSubscriptions.set(normalizedTenantId, currentCount - 1);
    }
  };
};

export const subscribeToConversation = async ({
  tenantId,
  conversationId,
  onMessageCreated,
  onConversationUpdated,
  onAiThinkingStep,
  onReconnected,
}: SubscribeOptions): Promise<() => Promise<void>> => {
  const hub = ensureConnection();
  const normalizedTenantId = tenantId.trim();

  if (!normalizedTenantId) {
    throw new Error('Realtime subscribe failed: tenant id is missing.');
  }

  await startConnection();

  const subscriptionKey = getSubscriptionKey(normalizedTenantId, conversationId);
  activeSubscriptions.set(subscriptionKey, (activeSubscriptions.get(subscriptionKey) || 0) + 1);

  if (onReconnected) {
    reconnectHandlers.set(subscriptionKey, onReconnected);
  }

  const messageHandler = (raw: any) => {
    const evt = normalizeMessageCreated(raw);
    onMessageCreated(evt);
  };

  const conversationHandler = (raw: any) => {
    const evt = normalizeConversationUpdated(raw);
    onConversationUpdated(evt);
  };

  const aiThinkingStepHandler = (raw: any) => {
    if (!onAiThinkingStep) return;
    onAiThinkingStep({
      tenantId: String(raw?.tenantId ?? ''),
      conversationId: String(raw?.conversationId ?? ''),
      runId: raw?.runId == null ? null : String(raw.runId),
      step: String(raw?.step ?? ''),
      stepKey: raw?.stepKey == null ? null : String(raw.stepKey),
      detail: raw?.detail == null ? null : String(raw.detail),
      timestamp: String(raw?.timestamp ?? new Date().toISOString()),
    });
  };

  hub.on('messageCreated', messageHandler);
  hub.on('conversationUpdated', conversationHandler);
  hub.on('aiThinkingStep', aiThinkingStepHandler);

  try {
    await hub.invoke('SubscribeConversation', normalizedTenantId, conversationId);
  } catch (error) {
    hub.off('messageCreated', messageHandler);
    hub.off('conversationUpdated', conversationHandler);
    hub.off('aiThinkingStep', aiThinkingStepHandler);

    const currentCount = activeSubscriptions.get(subscriptionKey) || 0;
    if (currentCount <= 1) {
      activeSubscriptions.delete(subscriptionKey);
      reconnectHandlers.delete(subscriptionKey);
    } else {
      activeSubscriptions.set(subscriptionKey, currentCount - 1);
    }
    throw toRealtimeError('subscribe', error);
  }

  return async () => {
    hub.off('messageCreated', messageHandler);
    hub.off('conversationUpdated', conversationHandler);
    hub.off('aiThinkingStep', aiThinkingStepHandler);

    const currentCount = activeSubscriptions.get(subscriptionKey) || 0;
    if (currentCount <= 1) {
      activeSubscriptions.delete(subscriptionKey);
      reconnectHandlers.delete(subscriptionKey);
      if (hub.state === HubConnectionState.Connected) {
        try {
          await hub.invoke('UnsubscribeConversation', normalizedTenantId, conversationId);
        } catch (error) {
          console.warn('[conversationHub] unsubscribe failed', { tenantId: normalizedTenantId, conversationId, error });
        }
      }
    } else {
      activeSubscriptions.set(subscriptionKey, currentCount - 1);
    }
  };
};
